/**
 * A KPI workflow, inside the shell — `/workflows/kpi/:workflowId` (DOP-479).
 *
 * The old `/workflow` was the grid and nothing else: no name, no way to run
 * it, no history, no statement of what it was configured to extract. Those
 * facts existed on the Templates page, one navigation away from the numbers
 * they explained. A workflow is the configured template AND its output AND
 * its run history, so all four are tabs of one page here.
 *
 * The grid itself is scoped to this workflow (DOP-476): `useKpiTable` takes the
 * workflow id, and the property strip is narrowed to the properties that
 * actually have values under it.
 *
 * Every view lives in the URL — `?tab=` for the shell's section, `?view=` for
 * the property, `?grain=` for the bucket, `?thread=` for the open Analyst
 * thread — and is read straight from it (decision D6), so a link opens on
 * exactly what it names and nothing is mirrored into state to drift.
 */

import {
	ChevronDown,
	Clock,
	Download,
	Flag,
	LineChart,
	MessageSquare,
	Play,
	RefreshCw,
	Table2,
} from "lucide-react";
import React, {
	Suspense,
	lazy,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { useParams, useSearchParams } from "react-router-dom";

import DashboardShell, {
	SHELL_TAB_PARAM,
	type DashboardShellProps,
} from "../../components/Dashboards/DashboardShell";
import type { PageTab } from "../../components/Dashboards/PageTabs";
import KpiDataBody from "../../components/Kpi/KpiDataBody";
import KpiHistoryTab from "../../components/Kpi/KpiHistoryTab";
import KpiNeedsReviewTab from "../../components/Kpi/KpiNeedsReviewTab";
import type { KpiCellCoords } from "../../components/Kpi/KpiGrid";
import { buildDashboardContext } from "../../components/Kpi/kpiChatBridge";
import { tableWithoutEmptyRows } from "../../components/Kpi/kpiEmptyRow";
import { formatPeriodRange } from "../../components/Kpi/kpiFormat";
import { needsReviewItems } from "../../components/Kpi/kpiNeedsReview";
import { contributingProperties } from "../../components/Kpi/kpiPropertyTabs";
import { downloadKpiTable } from "../../components/Kpi/kpiSpreadsheet";
import { useKpiTable } from "../../components/Kpi/useKpiTable";
import WorkflowToast from "../../components/Workflows/WorkflowToast";
import { PdfViewerProvider } from "../../context/PdfViewerContext";
import { SpreadsheetViewerProvider } from "../../context/SpreadsheetViewerContext";
import { useHideEmptyRows } from "../../hooks/useHideEmptyRows";
import { useKpiLayout } from "../../hooks/useKpiLayout";
import { useKpiOnboarding } from "../../hooks/useKpiOnboarding";
import { useKpiWorkflows } from "../../hooks/useKpiWorkflows";
import {
	type KpiPeriodGrain,
	isSupportedPeriodGrain,
} from "../../types/kpi";
import { INTERVAL_LABELS } from "../../types/kpiWorkflow";
import {
	DASHBOARD_GRAIN_PARAM,
	DASHBOARD_THREAD_PARAM,
	DASHBOARD_VIEW_PARAM,
	DASHBOARD_VIEW_PORTFOLIO,
} from "../../utils/chatHref";
import { isDashboardAnalystEnabled } from "../../utils/featureFlags";
import type { ChatPrefill } from "../Chat";
import RailFallback from "../../components/Chat/RailFallback";
import { useRailThread } from "../../hooks/useRailThread";

// The rail hosts the whole chat page. Loaded only when it opens, so the
// workflow does not carry the chat bundle for a control most visits never
// press.
const KpiAnalystRail = lazy(() => import("../../components/Kpi/KpiAnalystRail"));

const GRAIN_LABEL: Record<KpiPeriodGrain, string> = {
	month: "Monthly",
	quarter: "Quarterly",
	year: "Yearly",
};

const KpiDashboardPage: React.FC = () => {
	const { workflowId = "" } = useParams<{ workflowId: string }>();
	const [searchParams, setSearchParams] = useSearchParams();

	const {
		workflows,
		runsByWorkflow,
		loadingRunsFor,
		runningIds,
		isCheckingActivity,
		runNow,
		cancelRunNow,
		restoreVersionNow,
		loadRuns,
		toast,
		dismissToast,
	} = useKpiWorkflows();
	// The list is the only read of a single workflow there is — the API has no
	// `GET /workflows/{id}` — so the page finds itself in it rather than adding
	// an endpoint to save one request.
	const workflow = workflows.find((candidate) => candidate.id === workflowId) ?? null;

	// ---- the view, read straight from the URL -----------------------------

	const viewParam = searchParams.get(DASHBOARD_VIEW_PARAM);
	const propertyKey =
		!viewParam || viewParam === DASHBOARD_VIEW_PORTFOLIO ? null : viewParam;
	const grainParam = searchParams.get(DASHBOARD_GRAIN_PARAM);
	const grain: KpiPeriodGrain = isSupportedPeriodGrain(grainParam)
		? grainParam
		: "month";

	/**
	 * Replaced, not pushed. `?view=` and `?grain=` refine ONE page rather than
	 * moving between pages, and a history entry per grain click would make Back
	 * feel stuck two clicks from where the user came in. The shell's `?tab=` is
	 * the one that pushes.
	 */
	const setParam = useCallback(
		(key: string, value: string | null) => {
			setSearchParams(
				(prev) => {
					const next = new URLSearchParams(prev);
					if (value === null) next.delete(key);
					else next.set(key, value);
					return next;
				},
				{ replace: true },
			);
		},
		[setSearchParams],
	);

	const scope = useMemo(
		() => ({ workflowId, propertyKey, grain }),
		[workflowId, propertyKey, grain],
	);
	const data = useKpiTable(scope);
	const { table, loading, stale } = data;

	// Restoring a version changes the numbers on the other tabs, so the table
	// is re-read once it has happened — and only then.
	const refreshTable = data.refresh;
	const restoreRun = useCallback(
		async (targetWorkflowId: string, runId: string) => {
			if (await restoreVersionNow(targetWorkflowId, runId)) refreshTable();
		},
		[restoreVersionNow, refreshTable],
	);

	// ---- selection, held here because the Analyst descriptor reads it ------

	const [expanded, setExpanded] = useState<KpiCellCoords | null>(null);
	const [detailsFor, setDetailsFor] = useState<KpiCellCoords | null>(null);
	// ON by default, remembered per browser (DOP-293).
	const { hideEmptyRows, setHideEmptyRows } = useHideEmptyRows();
	const onboarding = useKpiOnboarding();
	const layout = useKpiLayout();

	// A cell selected on one scope does not exist on the next — October is not
	// Q4, so the coordinates an open expansion holds address nothing after a
	// switch. Keyed on the scope rather than on the click, because the scope
	// also changes when a link names another one.
	useEffect(() => {
		setExpanded(null);
		setDetailsFor(null);
	}, [propertyKey, grain]);

	const selectProperty = useCallback(
		(id: string) => setParam(DASHBOARD_VIEW_PARAM, id),
		[setParam],
	);
	const selectGrain = useCallback(
		(next: KpiPeriodGrain) => setParam(DASHBOARD_GRAIN_PARAM, next),
		[setParam],
	);

	// ---- header ------------------------------------------------------------

	const meta = useMemo(() => {
		if (!workflow) return undefined;
		const lastRun = workflow.last_run?.finished_at_ms ?? null;
		return [
			`${workflow.metric_keys.length} ${workflow.metric_keys.length === 1 ? "metric" : "metrics"}`,
			INTERVAL_LABELS[workflow.interval],
			formatPeriodRange(workflow.period_start, workflow.period_end),
			lastRun === null
				? "Never run"
				: `Last run ${new Date(lastRun).toLocaleDateString(undefined, {
						month: "short",
						day: "numeric",
						year: "numeric",
					})}`,
		].join(" · ");
	}, [workflow]);

	const isRunning = runningIds.has(workflowId);
	const [runMenuOpen, setRunMenuOpen] = useState(false);
	const runMenuRef = useRef<HTMLDivElement | null>(null);
	useEffect(() => {
		if (!runMenuOpen) return;
		const onKey = (event: KeyboardEvent) => {
			if (event.key === "Escape") setRunMenuOpen(false);
		};
		const onClick = (event: MouseEvent) => {
			if (!runMenuRef.current?.contains(event.target as Node)) setRunMenuOpen(false);
		};
		document.addEventListener("keydown", onKey);
		document.addEventListener("mousedown", onClick);
		return () => {
			document.removeEventListener("keydown", onKey);
			document.removeEventListener("mousedown", onClick);
		};
	}, [runMenuOpen]);
	// Nothing to export while the table is still arriving, when it has no metric
	// rows, or while the grid still shows the PREVIOUS scope — a download named
	// for the new scope carrying the old one's numbers is a wrong file, not a
	// slow one.
	const canDownload = Boolean(
		!loading &&
			!stale &&
			table &&
			table.periods.length > 0 &&
			table.groups.some((group) => group.metrics.length > 0),
	);
	const handleDownload = useCallback(() => {
		if (!table) return;
		// The toggle means the same thing on every surface the page has: a
		// download carrying rows the user chose to hide filtered only the screen.
		// In the dashboard's order too (DOP-613): the file should read like the
		// screen it was downloaded from.
		const arranged = layout.arrange(table);
		downloadKpiTable(hideEmptyRows ? tableWithoutEmptyRows(arranged) : arranged);
	}, [table, hideEmptyRows, layout]);

	const runDisabled = isCheckingActivity || isRunning || !workflow;
	const actions = (
		<>
			{/* The same split button the workflow card offers: Run now, and behind
			    the chevron the forced re-extract. Folded in rather than a second
			    button because force re-reads every file, including the ones the
			    coverage ledger already covers — it is the only way an extractor
			    improvement reaches a corpus that was extracted before it shipped,
			    and it costs a full set of model calls per file. */}
			<div className="relative" ref={runMenuRef}>
				<div className="flex">
					<button
						type="button"
						onClick={() => void runNow(workflowId)}
						disabled={runDisabled}
						title={
							isCheckingActivity
								? "Checking whether a run is already in progress…"
								: undefined
						}
						className="flex items-center gap-1.5 rounded-l-md bg-[#008782] px-3 py-1.5 text-sm font-medium text-white hover:bg-[#004C4B] disabled:opacity-50"
					>
						<Play className="h-4 w-4" aria-hidden="true" />
						{isRunning ? "Running…" : "Run now"}
					</button>
					<button
						type="button"
						onClick={() => setRunMenuOpen((open) => !open)}
						disabled={runDisabled}
						aria-haspopup="menu"
						aria-expanded={runMenuOpen}
						aria-label="More run options"
						className="inline-flex items-center rounded-r-md border-l border-[#00615D] bg-[#008782] px-2 py-1.5 text-white hover:bg-[#004C4B] disabled:opacity-50"
					>
						<ChevronDown className="h-4 w-4" aria-hidden="true" />
					</button>
				</div>
				{runMenuOpen && (
					<div
						role="menu"
						aria-label="Run options"
						className="absolute right-0 z-10 mt-1 w-64 rounded-lg border border-gray-200 bg-white py-1 shadow-lg"
					>
						<button
							type="button"
							role="menuitem"
							onClick={() => {
								setRunMenuOpen(false);
								void runNow(workflowId, true);
							}}
							className="flex w-full items-start gap-2 px-3 py-2 text-left text-sm text-text-base hover:bg-gray-50"
						>
							<RefreshCw className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
							<span>
								Force re-extract
								<span className="block text-xs text-text-muted">
									Re-reads every file, including ones already extracted
								</span>
							</span>
						</button>
					</div>
				)}
			</div>
			<button
				type="button"
				onClick={handleDownload}
				disabled={!canDownload}
				className="flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-text-base hover:bg-gray-50 disabled:opacity-50"
			>
				<Download className="h-4 w-4" aria-hidden="true" />
				Download Excel
			</button>
		</>
	);

	// ---- tabs --------------------------------------------------------------

	const reviewItems = useMemo(() => needsReviewItems(table), [table]);

	/**
	 * The narrowing, HELD across scope changes.
	 *
	 * Only the rollup carries contributors, so a property grid can say nothing
	 * about the roster — and letting the strip widen back to every property the
	 * account has, the moment a property tab is opened, would be a strip that
	 * changes shape as you walk it. Kept in a ref rather than in state so it
	 * applies in the SAME render the table arrives in; settling it in an effect
	 * would show the whole roster for one frame and then drop tabs out from
	 * under the pointer. The write is idempotent for a given table.
	 */
	const lastNarrowing = useRef<Set<string> | null>(null);
	const withValues = useMemo(() => {
		const contributing = contributingProperties(table);
		if (contributing) lastNarrowing.current = contributing;
		return lastNarrowing.current;
	}, [table]);

	const propertyTabs = useMemo<PageTab[]>(() => {
		const roster = table?.properties ?? [];
		return [
			{ id: DASHBOARD_VIEW_PORTFOLIO, label: "Portfolio" },
			...roster
				.filter((property) => !withValues || withValues.has(property))
				.map((property) => ({ id: property, label: property })),
		];
	}, [table, withValues]);

	/**
	 * No Settings tab: a KPI workflow's definition cannot be changed — there is
	 * no PATCH — so it was a page that only ever restated the header.
	 *
	 * Charts is a tab of its own (DOP-609). It was "Show trends", a button in
	 * the grid's toolbar that swapped the grid out; the Summit analyst loved
	 * the charts and had barely found them. It says "New" until it is opened.
	 * The Table keeps the id `data` so links and threads that name it still
	 * land on it.
	 */
	const { chartsSeen, markChartsSeen } = onboarding;
	const tabs = useMemo<PageTab[]>(
		() => [
			{ id: "data", label: "Table", icon: <Table2 className="h-4 w-4" aria-hidden="true" /> },
			{
				id: "charts",
				label: "Charts",
				icon: <LineChart className="h-4 w-4" aria-hidden="true" />,
				badge: chartsSeen ? undefined : "New",
			},
			{
				id: "review",
				label: "Needs review",
				count: reviewItems.length,
				countTone: "attention",
				icon: <Flag className="h-4 w-4" aria-hidden="true" />,
			},
			{ id: "history", label: "History", icon: <Clock className="h-4 w-4" aria-hidden="true" /> },
		],
		[reviewItems.length, chartsSeen],
	);

	// Opened once, the "New" badge has done its job.
	const activeTabParam = searchParams.get(SHELL_TAB_PARAM);
	useEffect(() => {
		if (activeTabParam === "charts" && !chartsSeen) markChartsSeen();
	}, [activeTabParam, chartsSeen, markChartsSeen]);


	/** A review row opens that cell's evidence, back on the Data tab. */
	const openCellOnData = useCallback(
		(metricKey: string, period: string) => {
			setExpanded({ metricKey, period });
			setDetailsFor({ metricKey, period });
			setParam(SHELL_TAB_PARAM, "data");
		},
		[setParam],
	);

	// ---- Analyst rail ------------------------------------------------------

	const analystEnabled = isDashboardAnalystEnabled();
	const {
		threadId: threadParam,
		open: analystOpen,
		setOpen: setAnalystOpen,
		nameThread,
		close: closeAnalyst,
	} = useRailThread(DASHBOARD_THREAD_PARAM);
	const [analystPrefill, setAnalystPrefill] = useState<ChatPrefill | null>(null);
	const askAnalyst = useCallback((question: string) => {
		setAnalystPrefill((prev) => ({ text: question, nonce: (prev?.nonce ?? 0) + 1 }));
		setAnalystOpen(true);
	}, [setAnalystOpen]);

	// The grain the descriptor reports is the one the numbers are IN. A grain
	// this build cannot name falls back to the request, the closest true
	// statement available.
	const tableGrain = table?.period_grain ?? "month";
	const contextGrain: KpiPeriodGrain = isSupportedPeriodGrain(tableGrain)
		? tableGrain
		: grain;
	const buildAnalystContext = useCallback(
		() =>
			buildDashboardContext({
				// Carried so the thread has a page to reopen at: `chatHref` sends a
				// kpi_dashboard thread to this workflow only when the persisted
				// surface_state names the workflow.
				workflowId,
				propertyKey,
				grain: contextGrain,
				hideEmptyRows,
				selected: detailsFor ?? expanded,
			}),
		[workflowId, propertyKey, contextGrain, hideEmptyRows, detailsFor, expanded],
	);

	const rightRail: DashboardShellProps["rightRail"] =
		analystEnabled && analystOpen ? (
			<Suspense fallback={<RailFallback />}>
				<KpiAnalystRail
					scopeLabel={propertyKey ?? "Portfolio"}
					grainLabel={GRAIN_LABEL[contextGrain]}
					buildContext={buildAnalystContext}
					prefill={analystPrefill}
					onClose={closeAnalyst}
					initialChatId={threadParam}
					onThreadChange={nameThread}
				/>
			</Suspense>
		) : undefined;

	return (
		// The same workbook viewer the chat mounts, hosted here too: the in-grid
		// expansions render cited cells through it, and without a provider in
		// scope they would fall back to a link that downloads the file whole.
		<SpreadsheetViewerProvider>
			<PdfViewerProvider>
				{/* A column, so the run toast is a strip above the shell rather
				    than a second item in its row. It renders nothing when there
				    is no message. */}
				<div className="flex h-full min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
				<div className="shrink-0 empty:hidden">
					<WorkflowToast toast={toast} onDismiss={dismissToast} />
				</div>
				<DashboardShell
					title={workflow?.name ?? "KPIs"}
					meta={meta}
					actions={actions}
					tabs={tabs}
					tabActions={
						analystEnabled ? (
							<button
								type="button"
								onClick={() => setAnalystOpen((open) => !open)}
								aria-pressed={analystOpen}
								className={`flex items-center gap-1.5 rounded border px-2 py-1 text-sm ${
									analystOpen
										? "border-[#008782] bg-[#E6F2F1] text-[#004C4B]"
										: "border-gray-300 text-text-base hover:bg-gray-50"
								}`}
							>
								<MessageSquare className="h-4 w-4" aria-hidden="true" />
								Chat
							</button>
						) : undefined
					}
					rightRail={rightRail}
				>
					{(tab) => {
						if (tab === "review") {
							return (
								<KpiNeedsReviewTab
									items={reviewItems}
									grain={tableGrain}
									scopeLabel={propertyKey ?? "the portfolio"}
									onOpenCell={openCellOnData}
								/>
							);
						}
						if (tab === "history") {
							return (
								<KpiHistoryTab
									workflowId={workflowId}
									runs={runsByWorkflow[workflowId]}
									loading={loadingRunsFor.has(workflowId)}
									onLoad={loadRuns}
									onCancelRun={cancelRunNow}
									onRestoreRun={restoreRun}
								/>
							);
						}
						return (
							<KpiDataBody
								view={tab === "charts" ? "charts" : "table"}
								data={data}
								propertyTabs={propertyTabs}
								activeProperty={propertyKey ?? DASHBOARD_VIEW_PORTFOLIO}
								onSelectProperty={selectProperty}
								grain={grain}
								onGrainChange={selectGrain}
								hideEmptyRows={hideEmptyRows}
								onHideEmptyRowsChange={setHideEmptyRows}
								onboarding={onboarding}
								layout={layout}
								expanded={expanded}
								onExpandCell={setExpanded}
								detailsFor={detailsFor}
								onOpenDetails={setDetailsFor}
								onAsk={analystEnabled ? askAnalyst : undefined}
							/>
						);
					}}
				</DashboardShell>
				</div>
			</PdfViewerProvider>
		</SpreadsheetViewerProvider>
	);
};

export default KpiDashboardPage;
